import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { DropdownCheckListCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/dropdownCheckListCustomFieldDefinition";
import type { DropdownCheckListCustomFieldValue } from "@btnet/services-btnet/handlers/models/dropdownCheckListCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { DropdownCheckListCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/DropdownCheckListCellEditor";
import { MultiSelectFieldEditor } from "commonComponents/entity/customField/utils/customFieldTypes/MultiSelectFieldEditor";
import {
    getOptionSortOrders,
    getOptionTexts,
    parseCommaSeparatedIds,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

export const DropdownCheckListCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    DropdownCheckListCustomFieldValue,
    DropdownCheckListCustomFieldDefinition
> = {
    getValue(field): string | null {
        const validIds = field.selectedOptionIds?.filter((id) => id > 0);
        return validIds?.length ? validIds.join(",") : "";
    },

    setValue(field, value): DropdownCheckListCustomFieldValue {
        const ids = value
            ? value
                  .split(",")
                  .map(Number)
                  .filter((n) => !isNaN(n))
            : [];
        return { ...field, selectedOptionIds: ids };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): DropdownCheckListCustomFieldValue {
        return {
            dataType: "DropDownCheckList",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedOptionIds: value
                ? value
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n))
                : [],
        };
    },

    compare(valueA: string | null, valueB: string | null, definition?, sortByOptionOrder?): number {
        const idsA = valueA ? parseCommaSeparatedIds(valueA) : [];
        const idsB = valueB ? parseCommaSeparatedIds(valueB) : [];

        if (sortByOptionOrder) {
            const sortOrdersA = getOptionSortOrders(idsA, definition);
            const sortOrdersB = getOptionSortOrders(idsB, definition);
            const maxLength = Math.max(sortOrdersA.length, sortOrdersB.length);
            for (let i = 0; i < maxLength; i++) {
                const a = sortOrdersA[i] ?? -1;
                const b = sortOrdersB[i] ?? -1;
                if (a !== b) return a - b;
            }
            return 0;
        }

        const textA = getOptionTexts(idsA, definition);
        const textB = getOptionTexts(idsB, definition);
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, definition): ReactNode {
        if (!value) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const ids = parseCommaSeparatedIds(value);
        if (ids.length === 0) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionTexts(ids, definition);
        return <BTText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: DropdownCheckListCustomFieldDefinition,
        _field?: DropdownCheckListCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        return (
            <MultiSelectFieldEditor
                customFieldId={customFieldId}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                definition={definition}
                testId="dropdownCheckListCustomFieldEditor"
                formMode={formMode}
            />
        );
    },

    getAgGridCellEditor() {
        return DropdownCheckListCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
